import React, { FC, useEffect, useMemo } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import TopBlock from './TopBlock';
import { RootStoreType } from 'src/types/store';
import { allColumns } from './constants';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType } from 'src/types/table';
import { useCheckPermission } from 'src/utils/checkPermission';
import { WebhookEditIcon } from 'src/components/SkladonIcon/Icons';
import { getWebhooksListFetch } from 'src/resources/webhooksList/reducer';
import SimpleSwitch from 'src/components/SimpleSwitch';
import {
    activateWebhookFetch,
    deactivateWebhookFetch,
} from 'src/resources/webhookDetail/reducer';
import { getWebhookMethodSelectOptions, getWebhookTypeSelectOptions } from 'src/utils/webhookSelectOptions';

const WebhooksListContainer: FC = () => {
    const dispatch = useDispatch();

    const loading = useSelector((state: RootStoreType) => state.webhooksList.loading);
    const webhooksData = useSelector((state: RootStoreType) => state.webhooksList.data);

    const permissionCreate = useCheckPermission('WEBHOOK.CREATE');
    const permissionDetail = useCheckPermission('WEBHOOK.DETAIL');
    const permissionUpdate = useCheckPermission('WEBHOOK.UPDATE');

    const webhookTypeCodes = useMemo(() => getWebhookTypeSelectOptions(), []);
    const webhookMethodCodes = useMemo(() => getWebhookMethodSelectOptions(), []);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('webhooks.title') }));
        dispatch(getWebhooksListFetch());
    }, [dispatch]);

    const reloadWebhooksList = () => {
        dispatch(getWebhooksListFetch());
    };

    const customRender: CustomRenderType = (column, row) => {
        switch (column.key) {
            case 'ACTIVE':
                return (
                    <SimpleSwitch
                        value={row.ACTIVE === 1}
                        onChange={(value) => {
                            if (value) {
                                dispatch(activateWebhookFetch(row.ID));
                            } else {
                                dispatch(deactivateWebhookFetch(row.ID));
                            }
                        }}
                        disabled={!permissionUpdate}
                    />
                );
            case 'PAYLOAD_VERSION':
                return translate(`webhooks.payloadVersion.${row.PAYLOAD_VERSION || 'v2'}`);
            default:
                return undefined;
        }
    };

    return (
        <div data-testid="webhooks-list">
            <TopBlock reloadWebhooksList={reloadWebhooksList} permissionCreate={permissionCreate} />
            <div data-testid="webhooks-list-table">
            <TableSimple
                rowIdKey="ID"
                rows={webhooksData || []}
                loading={loading}
                columsLabelPrefix="webhooks.table"
                allColumns={allColumns}
                initSort={{ column: 'REGISTERED_AT', direction: 'DESC' }}
                rowActions={[
                    {
                        icon: <WebhookEditIcon />,
                        label: translate('webhooks.action.webhookDetail'),
                        getLink: (row) => `/settings/webhooks/${row.ID}/`,
                        hiddenFce: () => !permissionDetail,
                    },
                ]}
                selectOptions={{
                    webhookTypeCodes,
                    webhookMethodCodes,
                    boolNumberCodes,
                }}
                heightVariant="top-tab-2"
                fullHeight
                optionsSessionName="webhooksTable"
                customRender={customRender}
            />
            </div>
        </div>
    );
};

export default WebhooksListContainer;
